import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React from 'react';

import { CompletionStatus, NarrativeState, Node } from '../../types/narrative';
import {
  getSiblingNodesInSegment,
  getWorldFromNodeId,
  isCurrentBranchComplete,
} from '../../utils/narrativeHelpers';
import { PathsToNode } from '../PathsToNode';
import { VariantContentRenderer } from '../VariantContentRenderer';
import { VariantTable } from '../VariantTable';

export interface NodeCardProps {
  node: Node;
  narrativeState: NarrativeState;
  isActive: boolean;
  isParent: boolean;
  isMiniaturized: boolean;
  isClickable?: boolean;
  shouldAnimate?: boolean;
  onClick: () => void;
  onChoiceClick?: (choiceIndex: number) => void;
}

const getNodeColors = (completionStatus: CompletionStatus) => {
  switch (completionStatus) {
    case 'complete':
      return {
        gradient: 'from-[#031209] via-[#051a0c] to-[#164a29]',
        border: 'border-[#059669]',
        titleGradient: 'from-[#059669] via-[#10b981] to-[#a7f3d0]',
        boxBg: 'bg-[#295e3f]',
        boxHover: 'hover:bg-[#059669]',
        glow: 'rgba(5, 150, 105, 0.3)',
      } as const;
    case 'partial':
      return {
        gradient: 'from-[#0c1222] to-[#1a2d4d]',
        border: 'border-[#4f46e5]',
        titleGradient: 'from-[#4f46e5] via-[#06b6d4] to-[#a5f3fc]',
        boxBg: 'bg-[#2a3756]',
        boxHover: 'hover:bg-[#4f46e5]',
        glow: 'rgba(79, 70, 229, 0.3)',
      } as const;
    default:
      return {
        gradient: 'from-[#120303] via-[#1a0505] to-[#3d1515]',
        border: 'border-[#dc2626]',
        titleGradient: 'from-[#dc2626] via-[#f43f5e] to-[#fecaca]',
        boxBg: 'bg-[#602929]',
        boxHover: 'hover:bg-[#dc2626]',
        glow: 'rgba(220, 38, 38, 0.3)',
      } as const;
  }
};

const getChoiceColor = (choice: { text: string }, parentComplete: boolean) => {
  if (!choice.text || choice.text.trim() === '') {
    return parentComplete ? 'bg-[#602929] border-[#dc2626]' : 'bg-[#2a3756] border-[#4f46e5]';
  }
  return 'bg-[#295e3f] border-[#059669]';
};

export const NodeCard = React.memo(function NodeCard({
  node,
  narrativeState,
  isActive,
  isParent,
  isMiniaturized,
  isClickable = true,
  shouldAnimate = false,
  onClick,
  onChoiceClick,
}: NodeCardProps) {
  const colors = getNodeColors(node.completionStatus || 'empty');
  const [isHovered, setIsHovered] = React.useState(false);

  // Determine which choices to display based on node ID
  const getDisplayChoices = () => {
    if (node.type === 'Conditional') {
      const world = getWorldFromNodeId(node.id);
      if (world === 'good') {
        return node.choicesGoodWorld || [];
      }
      if (world === 'bad') {
        return node.choicesBadWorld || [];
      }
    }
    return node.choices || [];
  };

  const displayChoices = getDisplayChoices();

  // Very small miniaturized nodes for tree view
  if (isMiniaturized && !isParent) {
    const displayText =
      node.text?.substring(0, 10) + (node.text && node.text.length > 10 ? '...' : '') || 'Empty';

    return (
      <>
        <motion.div
          className={`
            w-24 h-12 rounded relative text-xs
            bg-gradient-to-br ${colors.gradient} border ${colors.border}
            transition-all duration-200
            ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
            ${shouldAnimate && !isActive && node.completionStatus !== 'complete' ? 'animate-attention-pulse' : ''}
          `}
          style={{
            opacity: isActive ? 1 : isClickable ? 0.7 : 0.3,
            transform: isActive ? 'scale(1)' : 'scale(0.9)',
          }}
          onClick={isClickable ? onClick : undefined}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          whileHover={isClickable ? { scale: 0.95 } : {}}
        >
          <div className="p-1 h-full flex items-center justify-center">
            {!isClickable && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded">
                <svg
                  className="w-4 h-4 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
            )}
            <p className="text-white text-[10px] text-center truncate px-1">{displayText}</p>
          </div>
        </motion.div>

        {/* Hover Preview */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="absolute z-50 w-80 pointer-events-none"
              style={{
                top: '50%',
                left: '110%',
                transform: 'translateY(-50%)',
              }}
            >
              <div
                className={`
                bg-gradient-to-br ${colors.gradient} border ${colors.border}
                rounded-xl p-6 shadow-2xl
              `}
              >
                <h3
                  className={`text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r ${colors.titleGradient} mb-2 flex items-center gap-2`}
                >
                  PREVIEW: {node.idAlias || node.id.replace(/_/g, ' ')}
                  {!isClickable && (
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  )}
                </h3>
                <div className="text-white text-sm mb-3">
                  {node.text || 'No text entered yet...'}
                </div>
                {!isClickable && (
                  <div className="text-yellow-400 text-xs mb-2 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    Complete parent nodes first
                  </div>
                )}
                {displayChoices && displayChoices.length > 0 && (
                  <div className="space-y-1">
                    {displayChoices.map((choice, idx) => (
                      <div
                        key={idx}
                        className={`${colors.boxBg} text-white rounded px-3 py-1 text-xs`}
                      >
                        {choice.text || `Choice ${idx + 1}`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  // Parent node - smaller but with visible choices
  if (isParent) {
    return (
      <motion.div
        className={`
          bg-gradient-to-br ${colors.gradient} border ${colors.border}
          rounded-xl relative overflow-hidden shadow-xl transition-all duration-300
          p-6 cursor-pointer
        `}
        style={{
          opacity: 0.85,
          transform: 'scale(0.9)',
        }}
        onClick={onClick}
      >
        <div className="mb-4">
          <h3
            className={`font-bold text-transparent bg-clip-text bg-gradient-to-r ${colors.titleGradient} mb-1 text-lg`}
          >
            {node?.id ? `PREVIEW: ${node.id.replace(/_/g, ' ')}` : 'PREVIEW: Empty'}
          </h3>
          <div className="text-white leading-relaxed text-xs opacity-90">
            {node?.text || 'Your text will appear here...'}
          </div>
        </div>

        {displayChoices && displayChoices.length > 0 && (
          <div className="space-y-2">
            {displayChoices.map((choice, index) => {
              const choiceClasses = getChoiceColor(choice, node.completionStatus === 'complete');
              return (
                <motion.div
                  key={choice.id || index}
                  className={`
                    ${choiceClasses} border
                    text-white rounded-md px-4 py-2 text-xs transition-all cursor-pointer
                    hover:brightness-110
                  `}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChoiceClick?.(index);
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice.text || `Choice ${index + 1}`}</span>
                    <ChevronRight className="w-3 h-3 opacity-60" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>
    );
  }

  // Full-size active card
  return (
    <motion.div
      className={`
        bg-gradient-to-br ${colors.gradient} border ${colors.border}
        rounded-xl relative overflow-hidden shadow-xl transition-all duration-300
        p-8 cursor-pointer
      `}
      onClick={onClick}
      animate={{
        boxShadow: [`0 0 0px ${colors.glow}`, `0 0 20px ${colors.glow}`, `0 0 10px ${colors.glow}`],
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut',
      }}
      style={{ willChange: 'box-shadow' }}
    >
      <div className="mb-6">
        <h3
          className={`font-bold text-transparent bg-clip-text bg-gradient-to-r ${colors.titleGradient} mb-2 text-2xl`}
        >
          {node?.id ? `PREVIEW: ${node.idAlias || node.id.replace(/_/g, ' ')}` : 'PREVIEW: Empty'}
        </h3>
        {node?.descPreview && (
          <p className="text-gray-400 text-xs italic mb-3" style={{ whiteSpace: 'pre-line' }}>
            {node.descPreview.replace(/\n\n/g, '\n\n')}
          </p>
        )}
        <VariantTable nodeId={node.id} narrativeState={narrativeState} />
        <PathsToNode nodeId={node.id} narrativeState={narrativeState} />
        <div className="text-white leading-relaxed text-sm">
          <VariantContentRenderer node={node} field="text" />
        </div>
      </div>

      {displayChoices && displayChoices.length > 0 && (
        <>
          {node.completionStatus === 'complete' &&
            (() => {
              const hasAccessibleChild = displayChoices.some((choice) => {
                if (!choice.goto) return false;
                const targetNode = narrativeState.nodes[choice.goto];
                if (!targetNode) return false;
                if (!targetNode.parentIds || targetNode.parentIds.length === 0) return true;

                const targetSegment = targetNode.segmentId;

                return targetNode.parentIds.every((parentId: string) => {
                  const parentNode = narrativeState.nodes[parentId];
                  if (!parentNode) return false;

                  if (parentNode.segmentId === targetSegment) {
                    if (parentNode.completionStatus !== 'complete') {
                      return false;
                    }

                    if (parentNode.parentIds && parentNode.parentIds.length > 0) {
                      const siblings = getSiblingNodesInSegment(
                        narrativeState,
                        parentId,
                        targetSegment
                      );
                      return siblings.every((sibling) => sibling.completionStatus === 'complete');
                    }
                  }

                  return true;
                });
              });

              if (hasAccessibleChild) {
                return (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-3 text-center"
                  >
                    <motion.div
                      animate={{ y: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="inline-block"
                    >
                      <ChevronDown className="w-6 h-6 text-white mx-auto mb-1" />
                    </motion.div>
                    <p className="text-white text-sm font-medium">Choose a path to continue:</p>
                  </motion.div>
                );
              }
              // Check if current branch is complete for segment 3 final nodes
              const currentBranchComplete = isCurrentBranchComplete(narrativeState, node.id);
              const messageText = currentBranchComplete
                ? 'Fill the other branch to complete'
                : 'Complete sibling nodes before continuing';

              return (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-3 text-center"
                >
                  <div className="flex items-center justify-center gap-2 text-yellow-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <p className="text-sm">{messageText}</p>
                  </div>
                </motion.div>
              );
            })()}

          <div className="space-y-2">
            {displayChoices.map((choice, index) => {
              const choiceClasses = getChoiceColor(choice, node.completionStatus === 'complete');
              return (
                <motion.div
                  key={choice.id || index}
                  className={`
                    ${choiceClasses} border
                    text-white rounded-md px-5 py-3 text-sm transition-all cursor-pointer
                    hover:brightness-110
                  `}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChoiceClick?.(index);
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice.text || `Choice ${index + 1}`}</span>
                    <ChevronRight className="w-4 h-4 opacity-60" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </>
      )}
    </motion.div>
  );
});
